import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrainingMatrix = ({ employees, onViewEmployee, onUploadCertificate }) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'expired':
        return 'bg-error text-error-foreground';
      case 'expiring':
        return 'bg-warning text-warning-foreground';
      case 'current':
        return 'bg-success text-success-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getCompliancePercentage = (employee) => {
    const total = employee?.certifications?.length;
    const current = employee?.certifications?.filter(cert => cert?.status === 'current')?.length;
    return Math.round((current / total) * 100);
  };

  const filteredEmployees = employees?.filter(employee => {
    if (filterStatus === 'all') return true;
    return employee?.certifications?.some(cert => cert?.status === filterStatus);
  });

  const sortedEmployees = [...filteredEmployees]?.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'name':
        aValue = a?.name?.toLowerCase();
        bValue = b?.name?.toLowerCase();
        break;
      case 'department':
        aValue = a?.department?.toLowerCase();
        bValue = b?.department?.toLowerCase();
        break;
      case 'compliance':
        aValue = getCompliancePercentage(a);
        bValue = getCompliancePercentage(b);
        break;
      default:
        aValue = a?.[sortField];
        bValue = b?.[sortField];
    }

    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Training Compliance Matrix</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Track employee certifications and compliance status
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Status</option>
              <option value="current">Current</option>
              <option value="expiring">Expiring Soon</option>
              <option value="expired">Expired</option>
            </select>
            
            <Button variant="outline" iconName="Upload" iconPosition="left">
              Bulk Upload
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-clinical"
                >
                  <span>Employee</span>
                  <Icon 
                    name={sortField === 'name' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('department')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-clinical"
                >
                  <span>Department</span>
                  <Icon 
                    name={sortField === 'department' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-foreground">Required Certifications</span>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('compliance')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-clinical"
                >
                  <span>Compliance</span>
                  <Icon 
                    name={sortField === 'compliance' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployees?.map((employee) => (
              <tr key={employee?.id} className="border-b border-border hover:bg-muted/30 transition-clinical">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-foreground">
                        {employee?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{employee?.name}</p>
                      <p className="text-sm text-muted-foreground">{employee?.role}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground">{employee?.department}</span>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1">
                    {employee?.certifications?.slice(0, 3)?.map((cert, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(cert?.status)}`}
                      >
                        {cert?.name}
                      </span>
                    ))}
                    {employee?.certifications?.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        +{employee?.certifications?.length - 3} more
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          getCompliancePercentage(employee) >= 80 ? 'bg-success' :
                          getCompliancePercentage(employee) >= 60 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${getCompliancePercentage(employee)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {getCompliancePercentage(employee)}%
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onViewEmployee(employee)}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Upload"
                      onClick={() => onUploadCertificate(employee)}
                    >
                      Upload
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrainingMatrix;